import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { RouteContext } from 'src/context';
import { SettingsPageColumn } from 'src/features/settings/layout';
import { useCurrentSettingsPage } from 'src/features/settings/registry/useCurrentSettingsPage';
import {
  SettingsSaveBarProvider,
  SettingsSaveBarSlot,
} from 'src/features/settings/save-bar';

import { useHeaderSlot } from '../../AppShell/HeaderSlotContext';
import { useShell } from '../../AppShell/ShellContext';

export interface SettingsColumnVariantProps {
  children: React.ReactNode;
}

/**
 * The `new-settings` page layout: the 760px column under the title and
 * description from the page's registry entry. Sub-pages have no entry and keep
 * their route's title, as SettingsPageToolbar does.
 */
export const SettingsColumnVariant: React.FC<SettingsColumnVariantProps> = ({
  children,
}) => {
  const page = useCurrentSettingsPage();
  const { title: routeTitle, description: routeDescription } =
    React.useContext(RouteContext);
  const { drawerWidth, isResizableSidebarActive } = useShell();
  const { showSidebar } = useHeaderSlot();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  // The settings sidebar is fixed-position on desktop, so the content clears it.
  const paddingLeft =
    isMdUp && showSidebar && !isResizableSidebarActive ? drawerWidth : 0;

  return (
    <div
      // AppShell stops scrolling on phones, so the page scrolls itself there.
      className={`flex w-full flex-col ${isSmUp ? '' : 'overflow-y-auto'}`}
      style={{ paddingLeft }}
    >
      <SettingsSaveBarProvider>
        <SettingsSaveBarSlot />
        <SettingsPageColumn
          title={page?.label ?? routeTitle ?? ''}
          description={page?.description ?? routeDescription}
        >
          {children}
        </SettingsPageColumn>
      </SettingsSaveBarProvider>
    </div>
  );
};
